'use client';

import type { FC } from 'react';
import Image from 'next/image';
import { m, useReducedMotion } from 'motion/react';
import { usePathname } from 'next/navigation';

import { Heading } from '@/components/ui/heading/heading';

import type { TApiHeading, TApiImage, TApiText } from '@/shared/apiComponentTypes';
import { getImageSource } from '@/shared/image';

import { KeyPoint } from './key-point';
import { fadeSlideUp, scaleIn, staggerContainer } from '@/utils/animation/variants';

type TKeyPoint = { id: number; value: string; caption: string; mark: string };

type TKeyPointList = {
  __component: 'components.key-point-list';
  keyPoints: TKeyPoint[];
};

export type TKeyPointSection = [TApiHeading, TApiText, TKeyPointList, TApiImage];

export const KeyPointsSection: FC<TKeyPointSection> = props => {
  const backgroundImage = getImageSource(props[3].src, 'medium');
  const prefersReducedMotion = useReducedMotion();
  const pathname = usePathname();

  return (
    <m.div
      key={pathname}
      initial={prefersReducedMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
    >
      <m.div variants={fadeSlideUp} className="basis-1/2">
        <div className="inline-block mb-2 text-xs font-oswald uppercase tracking-[0.35em] text-neutral-400">
          <span className="font-oswald">{props[0].caption}</span>
          <div className="flex items-center gap-2 mt-2 w-3/4">
            <div className="h-1 basis-1/3 rounded-full bg-action" />
            <div className="h-[2px] basis-2/3 rounded-full bg-neutral-700/70" />
          </div>
        </div>

        <Heading variant="h2" className="mb-10" highlight={props[0].highlight}>
          {props[0].title}
        </Heading>
      </m.div>

      <div>
        <m.p variants={fadeSlideUp} className="md:max-w-[70%] text-neutral-300">
          {props[1].text}
        </m.p>

        <div className="relative">
          <m.div
            variants={staggerContainer}
            className="my-10 flex flex-col gap-4 md:flex-wrap md:flex-row md:gap-32"
          >
            {props[2].keyPoints.map(keyPoint => (
              <m.div key={keyPoint.id} variants={scaleIn}>
                <KeyPoint value={keyPoint.value} mark={keyPoint.mark} caption={keyPoint.caption} />
              </m.div>
            ))}
          </m.div>

          <div className="select-none top-1/2 right-0 w-full md:w-2/3 absolute -z-50 translate-x-1/3 -translate-y-1/2 scale-150">
            {backgroundImage && (
              <Image
                width={backgroundImage.width}
                height={backgroundImage.height}
                alt={backgroundImage.alt}
                src={backgroundImage.url}
              />
            )}
            <div className="absolute top-0 left-0 w-full h-full bg-background opacity-85" />
          </div>
        </div>
      </div>
    </m.div>
  );
};
